import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { SummariesResultsAvailablePayload } from "@app/notifications/message";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { BasicRecord, ByCountyRecord, getViewNameFromKey, OverallRecord, SummaryResultNetworkType, SummaryResultScope, SummaryResultType, ViewSelectorKey } from "@app/shared/data-summaries";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";
import { withZipContextState } from "@app/projects/zip-projects/ZipProjectContext";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { DataSummaries } from "@app/shared/data-summaries/DataSummaries";
import { SummaryDetailsRouteMatch } from "@app/projects/network-assessment/summary/interfaces";
import SummaryService from "@app/projects/network-assessment/summary/summaryService";

type SummaryDetailsProps = RouteComponentProps<SummaryDetailsRouteMatch> & ZipContextState & CountyProjectContextProps & NotificationContextProps;

interface SummaryDetailsState {
  activeScope: SummaryResultScope;
  activeView: ViewSelectorKey;

  addressRecords: BasicRecord[];
  byCountyRecords: ByCountyRecord[];
  byGeocodeRecords: BasicRecord[];
  bySpecialtyRecords: BasicRecord[];
  byStateRecords: BasicRecord[];
  forceUpdateRecords: boolean;
  loading: boolean;
  npiRecords: BasicRecord[];
  overallRecords: OverallRecord[];
  specialtyRecords: BasicRecord[];
}

class SummaryDetails extends Component<SummaryDetailsProps, SummaryDetailsState> {
  private defaultView = getViewNameFromKey(this.props.match.params.resultType) || ViewSelectorKey.Overall;
  private readonly summariesService = new SummaryService();

  constructor(props: SummaryDetailsProps) {
    super(props);

    this.state = {
      activeScope: SummaryResultScope.Network,
      activeView: this.defaultView,

      addressRecords: [],
      byCountyRecords: [],
      byGeocodeRecords: [],
      bySpecialtyRecords: [],
      byStateRecords: [],
      forceUpdateRecords: false,
      loading: false,
      npiRecords: [],
      overallRecords: [],
      specialtyRecords: [],
    };
  }

  public componentDidMount() {
    this.setState(({ activeScope: SummaryResultScope.Network, activeView: this.defaultView }));
    this.fetchRecords();
    this.props.subscribe(this.handleNotifications);
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
  }

  public componentDidUpdate(prevProps: SummaryDetailsProps, prevState: SummaryDetailsState) {
    if (prevState.activeScope !== this.state.activeScope || this.state.forceUpdateRecords) {
      this.setState({ forceUpdateRecords: false });
      this.fetchRecords();
    }
  }

  public render() {
    return (
      <DataSummaries
        activeScope={this.state.activeScope}
        defaultView={this.defaultView}
        handleScopeChange={this.handleScopeChange}
        providers={this.makeProvidersEndpoint}
        overallRecords={this.state.overallRecords}
        bySpecialtyRecords={this.state.bySpecialtyRecords}
        byCountyRecords={this.state.byCountyRecords}
        byStateRecords={this.state.byStateRecords}
        byGeocodeRecords={this.state.byGeocodeRecords}
        npiRecords={this.state.npiRecords}
        specialtyRecords={this.state.specialtyRecords}
        addressRecords={this.state.addressRecords}
        match={this.props.match}
        history={this.props.history}
        location={this.props.location}
        summariesLoading={this.state.loading}
      />
    );
  }

  private makeProvidersEndpoint = (networkType: SummaryResultNetworkType, resultType: SummaryResultType, key: string | number) => {
    if (!this.props.match.params.resultType) {
      return `${this.props.match.url}/${this.props.runConfiguration.projectRunConfigurationId}/${encodeURIComponent(this.state.activeScope)}/${encodeURIComponent(networkType)}/${encodeURIComponent(resultType)}/${encodeURIComponent(key.toString())}`;
    } else {
      let base = this.props.match.url;
      base = base.slice(0, base.lastIndexOf("/"));
      return `${base}/${this.props.runConfiguration.projectRunConfigurationId}/${encodeURIComponent(this.state.activeScope)}/${encodeURIComponent(networkType)}/${encodeURIComponent(resultType)}/${encodeURIComponent(key.toString())}`;
    }
  }

  private handleScopeChange = (newScope: DropdownItem<SummaryResultScope>) => {
    this.setState({ activeScope: newScope.key });
  }

  /** Triggers all API calls and updates the state as each one completes. Calls take place in parallel and completion order is not guaranteed. */
  private fetchRecords() {
    const runConfigurationId = this.props.runConfiguration && this.props.runConfiguration.projectRunConfigurationId;
    type tableType = OverallRecord | BasicRecord | ByCountyRecord;
    interface ReturnType {
      addressRecords: tableType[];
      byCountyRecords: tableType[];
      byGeocodeRecords: tableType[];
      bySpecialtyRecords: tableType[];
      byStateRecords: tableType[];
      npiRecords: tableType[];
      overallRecords: tableType[];
      specialtyRecords: tableType[];
    }
    this.setState({
      loading: true,
    }, () => {
      const summariesPromises: Array<Promise<Partial<ReturnType>>> = [
        this.summariesService.getOverall(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((overallRecords) => {
          return { overallRecords };
        }),
        this.summariesService.getBySpecialty(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((bySpecialtyRecords) => {
          return { bySpecialtyRecords };
        }),
        this.summariesService.getByCounty(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((byCountyRecords) => {
          return { byCountyRecords };
        }),
        this.summariesService.getByState(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((byStateRecords) => {
          return { byStateRecords };
        }),
        this.summariesService.getByGeocode(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((byGeocodeRecords) => {
          return { byGeocodeRecords };
        }),
        this.summariesService.getByNPIStatus(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((npiRecords) => {
          return { npiRecords };
        }),
        this.summariesService.getByNumSpecialties(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((specialtyRecords) => {
          return { specialtyRecords };
        }),
        this.summariesService.getByNumAddresses(this.props.project.projectId, runConfigurationId, this.state.activeScope).then((addressRecords) => {
          return { addressRecords };
        }),
      ];

      Promise.all(summariesPromises).then((results) => {
        const resultsObj: Partial<ReturnType> = {};
        results.forEach((r) => {
          const entries = Object.entries(r);
          Object.assign(resultsObj, { [entries[0][0]]: entries[0][1] });
        });
        this.setState({
          addressRecords: resultsObj.addressRecords as BasicRecord[],
          byCountyRecords: resultsObj.byCountyRecords as ByCountyRecord[],
          byGeocodeRecords: resultsObj.byGeocodeRecords as BasicRecord[],
          bySpecialtyRecords: resultsObj.bySpecialtyRecords as BasicRecord[],
          byStateRecords: resultsObj.byStateRecords as BasicRecord[],
          loading: false,
          npiRecords: resultsObj.npiRecords as BasicRecord[],
          overallRecords: resultsObj.overallRecords as OverallRecord[],
          specialtyRecords: resultsObj.specialtyRecords as BasicRecord[],
        });
      });
    });

  }

  private handleNotifications = (message: Message) => {
    if (message) {
      switch (message.notificationType as MessageType) {
        case MessageType.SummariesResultsAvailable:
          const summariesResultsPayload = message.payload as SummariesResultsAvailablePayload;

          if (this.props.project &&
            this.props.project.projectId === summariesResultsPayload.projectId &&
            this.props.runConfiguration &&
            this.props.runConfiguration.projectRunConfigurationId === summariesResultsPayload.projectRunConfigurationId) {
            this.setState({ forceUpdateRecords: true });
          }
          break;
      }
    }
  }
}

const WrappedSummaryDetails = withZipContextState(withCountyProjectContext(withNotificationContext(SummaryDetails)));

export { WrappedSummaryDetails as SummaryDetails };
